import React, { useState, useEffect } from 'react';
import { X, ArrowRight, Camera } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import BarcodeScanner from './BarcodeScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchOverlay = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        if (/^\d{8,13}$/.test(query)) {
          try {
            const { data: product } = await axios.get(`${API_URL}/api/search/barcode?code=${encodeURIComponent(query)}`);
            if (product) { setResults([product]); return; }
          } catch {}
        }
        const { data } = await axios.get(`${API_URL}/api/search/autocomplete?q=${encodeURIComponent(query)}`);
        setResults(data);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleClick = (id) => {
    navigate(`/products/${id}`);
    onClose();
    setQuery('');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.length >= 2) {
      navigate(`/products?search=${encodeURIComponent(query)}`);
      onClose();
      setQuery('');
    }
  };

  // Escape-to-close for accessibility + reliable dismissal
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);


  const handleScannerResult = async (code) => {
    try {
      const { data: product } = await axios.get(`${API_URL}/api/search/barcode?code=${encodeURIComponent(code)}`);
      if (product?.id) {
        navigate(`/products/${product.id}`);
        onClose();
        setQuery('');
        return;
      }
    } catch {}
    setQuery(code);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[600] bg-[#FAF6EF]/98 backdrop-blur-3xl search-overlay-luxe flex flex-col"
      data-testid="search-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <BarcodeScanner open={showScanner} onClose={() => setShowScanner(false)} onDetected={handleScannerResult} />
      <div className="max-w-3xl w-full mx-auto px-6 pt-24 md:pt-32">
        <div className="flex items-center justify-between mb-16">
          <span className="overline">Search</span>
          <div className="flex items-center gap-4">
            <button onClick={() => setShowScanner(true)} className="flex items-center gap-2 text-[#595956] hover:text-[#B08D57] transition-colors text-[10px] uppercase tracking-[0.2em] font-semibold" data-testid="search-camera-btn">
              <Camera size={16} strokeWidth={1.5} /> Scan
            </button>
            <button onClick={onClose} className="text-[#595956] hover:text-[#1B1815] transition-colors" data-testid="close-search-button">
              <X size={24} strokeWidth={1} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSearch}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products or scan barcode..."
            className="w-full bg-transparent border-0 border-b border-[#E5E0D8] text-3xl md:text-5xl text-[#1B1815] focus:outline-none focus:border-[#B08D57] transition-colors pb-4 placeholder:text-[#E5E0D8]"
            style={{fontFamily: "'Manrope', sans-serif"}}
            autoFocus
            data-testid="search-input"
          />
        </form>

        {results.length > 0 && (
          <div className="mt-12 space-y-0 divide-y divide-[#E5E0D8]" data-testid="search-results">
            {results.map((product) => (
              <button
                key={product.id}
                onClick={() => handleClick(product.id)}
                className="flex items-center gap-6 w-full py-5 hover:bg-[#F6F3EE] transition-colors px-2 text-left group"
                data-testid={`search-result-${product.id}`}
              >
                <img src={product.images?.[0]} alt={product.name} loading="lazy" decoding="async" className="w-16 h-20 object-cover bg-[#F6F3EE]" />
                <div className="flex-1">
                  <p className="text-sm font-medium tracking-tight">{product.name}</p>
                  <p className="text-xs text-[#595956] mt-0.5">{product.brand} &mdash; {product.category}</p>
                </div>
                <span className="text-sm text-[#595956]" style={{fontFamily: "'Manrope', sans-serif"}}>₹{product.price?.toFixed(2)}</span>
                <ArrowRight size={16} strokeWidth={1} className="text-[#E5E0D8] group-hover:text-[#B08D57] transition-colors" />
              </button>
            ))}
          </div>
        )}

        {query.length >= 2 && results.length === 0 && (
          <p className="mt-12 overline">No results found for "{query}"</p>
        )}
      </div>
    </div>
  );
};

export default SearchOverlay;
